<script lang="ts">
import { useTailwind } from '@/plugins/useTailwind'
import type { TIconValue } from '@/utils/helper-objects'
import { useResizeObserver, useScroll } from '@vueuse/core'
import { type PropType, defineComponent, useTemplateRef } from 'vue'
import type { RouteLocationRaw } from 'vue-router'

import CarouselHeader from '../CarouselHeader.vue'
import CarouselPagination from '../CarouselPagination.vue'

export const CarouselProps = {
  /**
   * The icon to display in the carousel header. If not provided, the header will not be shown.
   *
   * @type {TIconValue}
   */
  icon: {
    type: String as PropType<TIconValue> | null,
  },

  /**
   * The title to display in the carousel header. If not provided, the header will not be shown.
   *
   * @type {string}
   * @default null
   */
  title: {
    type: String,
    required: false,
  },

  /**
   * Additional classes to apply to the scrollbox frame element.
   * This can be used to apply custom styles to the scrollbox.
   *
   * @type {string}
   * @default ''
   */
  frameClass: {
    type: String,
    default: '',
  },
  titleClass: {
    type: String,
    default: '',
  },
  showIndicators: {
    type: Boolean,
    default: false,
  },

  /**
   * The route to navigate to when the "View All" link is clicked. If not provided, the "View All" link will not be shown.
   *
   * @type {RouteLocationRaw}
   */
  viewAllRoute: {
    type: Object as PropType<RouteLocationRaw>,
  },

  /**
   * Determines if the navigation arrows should be shown.
   * If `true`, arrows will be shown and can be used to navigate the carousel.
   *
   * @type {boolean}
   * @default true
   */
  showNavigation: {
    type: Boolean,
    default: true,
  },

  /**
   * The number of columns to display in the grid.
   *
   * @type {number}
   * @default isXl: 4, isMd: 3, else: 2
   */
  cols: {
    type: Number,
    required: false,
  },

  /**
   * The number of rows to display in the grid.
   *
   * @type {number}
   * @default isXl: 2, isMd: 2, else: 2
   */
  rows: {
    type: Number,
    required: false,
  },
  /**
   * The flow of the grid, either 'row' or 'column'.
   * If 'row', items will fill each row before creating a new one.
   * If 'column', items will fill each column before creating a new one.
   *
   * @type {'row' | 'column'}
   * @default 'row'
   */
  flow: {
    type: String as PropType<'column' | 'row'>,
    default: 'row',
  },

  /**
   * When true, items are grouped into discrete pages (cols x rows each) that
   * fill in reading order (left-to-right, top-to-bottom) and snap one page at
   * a time — rather than the default single continuous grid that fills the
   * whole top row first. Use for a "2x2 paginated" swipe layout.
   *
   * @type {boolean}
   * @default false
   */
  paged: {
    type: Boolean,
    default: false,
  },

  /**
   * The items to display in the carousel.
   * This can be any array of data, and the rendering of each item is handled by the default slot.
   *
   * @type {Array<unknown>}
   * @default []
   */
  items: {
    type: Array as PropType<Array<unknown>>,
    default: () => [],
    required: true,
  },
  /**
   * The padding inside the carousel in CSS units.
   * This is the space between the carousel content and the edges of the scrollbox.
   *
   * @type {string}
   * @default '16px'
   */
  padding: {
    type: String,
    default: '16px',
    validate: (value: string) => {
      return value.match(/^(\d+(?:\.\d+)?)(px|em|rem|%)$/) !== null
    },
  },
}

export type TCarouselProps = typeof CarouselProps

/**
 * AppGridCarousel
 */
export default defineComponent({
  components: {
    CarouselHeader,
    CarouselPagination,
  },
  props: {
    ...CarouselProps,
  },
  setup() {
    const wrapper = useTemplateRef<HTMLDivElement>('wrapper')
    const { arrivedState, measure, x } = useScroll(wrapper, {
      behavior: 'smooth',
    })

    const { stop: stopResizeObserver } = useResizeObserver(wrapper, measure)

    return {
      arrivedState,
      wrapper,
      stopResizeObserver,
      tailwind: useTailwind('main'),
      x,
    }
  },
  computed: {
    totalPages(): number {
      if (!this.wrapper) {
        return 1
      }
      return Math.max(
        1,
        Math.round(this.wrapper.scrollWidth / this.wrapper.clientWidth)
      )
    },
    currentPageIndex(): number {
      if (!this.wrapper || this.wrapper.clientWidth === 0) {
        return 0
      }
      return Math.round(this.x / this.wrapper.clientWidth)
    },
    /**
     * Determines if the scrollbox is at the start of its travel
     * and therefore cannot scroll left.
     */
    atStart(): boolean {
      return this.arrivedState.left
    },

    /**
     * Determines if the scrollbox is at the end of its travel
     * and therefore cannot scroll right.
     */
    atEnd(): boolean {
      return this.arrivedState.right
    },

    /**
     * Calculates the necessary grid styles based on the number of columns, rows, and flow direction.
     * This ensures that the carousel items are displayed in a responsive grid layout.
     */
    gridStyles() {
      if (this.flow === 'row') {
        return {
          // The grid template columns are calculated using container query units,
          // to fit the specified number of columns per page, accounting for gaps and padding.
          gridTemplateColumns: `repeat(${this.actualColCount}, calc((100cqw - (var(--gap) * (var(--cols-per-page) - 1)) - (var(--padding) * 2)) / var(--cols-per-page)))`,
          gridAutoFlow: this.flow,
        }
      } else {
        return {
          // The grid template rows are calculated using container query units,
          // to fit the specified number of rows per page, accounting for gaps and padding.
          gridTemplateRows: `repeat(var(--rows-per-page), 1fr)`,
          gridAutoColumns: `calc((100cqw - (var(--gap) * (var(--cols-per-page) - 1)) - (var(--padding) * 2)) / var(--cols-per-page))`,
          gridAutoFlow: this.flow,
        }
      }
    },

    getGridLayout() {
      if (this.cols && this.rows) {
        return { cols: this.cols, rows: this.rows }
      } else {
        return this.defaultGridLayout()
      }
    },

    /**
     * Calculates the actual number of columns needed to display the items.
     */
    actualColCount(): number {
      if (!this.hasItems) {
        return 0
      }

      return Math.max(
        this.getGridLayout.cols,
        Math.ceil(this.items.length / this.actualRowCount)
      )
    },

    /**
     * Calculates the actual number of rows needed to display the items.
     */
    actualRowCount(): number {
      if (!this.hasItems) {
        return 0
      }

      return Math.min(
        this.getGridLayout.rows,
        Math.ceil(this.items.length / this.getGridLayout.cols)
      )
    },

    /**
     * Calculates the number of empty cells needed to fill the carousel.
     */
    emptyCellCount() {
      if (!this.hasItems) {
        return 0
      }

      if (this.flow === 'row') {
        const totalCells = this.actualRowCount * this.actualColCount
        return Math.max(0, totalCells - this.items.length)
      } else {
        const totalCells =
          this.getGridLayout.rows *
          Math.ceil(this.items.length / this.getGridLayout.rows)
        return Math.max(0, totalCells - this.items.length)
      }
    },
    hasItems(): boolean {
      return Array.isArray(this.items) && this.items.length > 0
    },

    /**
     * The number of cells on a full page (`cols * rows`). Used when `paged`.
     */
    pageSize(): number {
      return this.getGridLayout.cols * this.getGridLayout.rows
    },

    /**
     * Items chunked into pages of `pageSize`, each filled in reading order.
     * Only used when `paged` is true.
     */
    pages(): Array<Array<unknown>> {
      if (!this.hasItems || this.pageSize < 1) {
        return []
      }

      const chunked: Array<Array<unknown>> = []
      for (let i = 0; i < this.items.length; i += this.pageSize) {
        chunked.push(this.items.slice(i, i + this.pageSize))
      }
      return chunked
    },
  },
  beforeUnmount() {
    this.stopResizeObserver()
  },
  methods: {
    /**
     * The number of unfilled cells on a given page — non-zero only for a
     * trailing page whose item count doesn't fill `pageSize`. Mirrors the
     * `emptyCellCount` the non-paged grid exposes, so the `empty` slot and the
     * slot's `empty-cell-count` behave consistently in paged mode.
     */
    pageEmptyCount(page: Array<unknown>): number {
      return Math.max(0, this.pageSize - page.length)
    },

    /**
     * Scrolls the carousel in the specified direction by a single column.
     *
     * @param {'next' | 'previous'} direction - The direction to scroll the carousel. 'next' for forward, 'previous' for backward.
     */
    scrollBy(direction: 'next' | 'previous') {
      if (!this.wrapper) {
        return 0
      }

      const cellWidth =
        this.wrapper.querySelector('li')?.clientWidth ??
        this.wrapper.clientWidth
      const scrollLeft = this.wrapper.scrollLeft

      const scrollAmount =
        direction === 'next' ? scrollLeft + cellWidth : scrollLeft - cellWidth

      this.wrapper.scrollTo({
        left: scrollAmount,
        behavior: 'smooth',
      })
    },

    /**
     * Steps forward one column.
     */
    nextPage() {
      this.scrollBy('next')
    },

    /**
     * Steps backwards by one column.
     */
    previousPage() {
      this.scrollBy('previous')
    },

    /**
     * Scrolls to the start of the carousel
     */
    scrollToStart() {
      this.wrapper?.scrollTo({
        left: 0,
        behavior: 'smooth',
      })
    },
    scrollToPage(index: number) {
      if (!this.wrapper) {
        return
      }
      this.wrapper.scrollTo({
        left: index * this.wrapper.clientWidth,
        behavior: 'smooth',
      })
    },

    defaultGridLayout() {
      if (this.tailwind.isLg.value) {
        return { cols: 4, rows: 2 }
      } else if (this.tailwind.isSm.value) {
        return { cols: 3, rows: 2 }
      } else {
        return { cols: 2, rows: 2 }
      }
    },
  },
})
</script>

<template>
  <div
    class="carousel"
    :class="['flex flex-col gap-4']"
    :style="{
      '--cols-per-page': getGridLayout.cols,
      '--rows-per-page': actualRowCount,
    }"
  >
    <CarouselHeader
      :icon
      :title
      :title-class="titleClass"
      :view-all-route="viewAllRoute"
      :show-navigation="showNavigation"
      :next-disabled="atEnd"
      :previous-disabled="atStart"
      @next-click="nextPage"
      @previous-click="previousPage"
    >
      <template #view-all-route>
        <slot name="view-all-route" :view-all-route="viewAllRoute"></slot>
      </template>
    </CarouselHeader>

    <slot name="preamble" />

    <ul
      v-if="paged"
      ref="wrapper"
      class="carousel__scrollbox carousel__scrollbox--paged scrollbar-none"
      :class="frameClass"
    >
      <li
        v-for="(page, pageIndex) of pages"
        :key="pageIndex"
        class="carousel__page"
        :style="{
          gridTemplateColumns: `repeat(${getGridLayout.cols}, 1fr)`,
        }"
      >
        <slot
          v-for="(item, index) of page"
          :key="`${pageIndex}-${index}`"
          :item="item"
          :empty-cell-count="pageEmptyCount(page)"
        />

        <div
          v-if="pageEmptyCount(page) > 0"
          :style="{
            gridColumn: `span ${Math.min(pageEmptyCount(page), getGridLayout.cols)}`,
          }"
        >
          <slot name="empty" :empty-cell-count="pageEmptyCount(page)" />
        </div>
      </li>
    </ul>

    <ul
      v-else
      ref="wrapper"
      class="carousel__scrollbox scrollbar-none"
      :style="gridStyles"
      :class="frameClass"
    >
      <li v-for="(item, index) of items" :key="index">
        <slot :key="index" :item="item" :empty-cell-count="emptyCellCount" />
      </li>

      <li
        v-if="emptyCellCount > 0"
        :style="{
          [`grid-${flow === 'row' ? 'column' : 'row'}`]: `span ${emptyCellCount}`,
        }"
      >
        <slot name="empty" :empty-cell-count="emptyCellCount" />
      </li>
    </ul>

    <CarouselPagination
      v-if="showIndicators && totalPages > 1"
      :count="totalPages"
      :current-index="currentPageIndex"
      @scroll-to="scrollToPage($event)"
    />
  </div>
</template>

<style lang="scss" scoped>
.carousel {
  @apply min-h-0 h-fit;

  container-type: inline-size;
  --cols-per-page: 1;
  --rows-per-page: 1;
  --gap: 16px;
  --padding: v-bind('padding');
}

.carousel__scrollbox {
  @apply overflow-x-auto min-h-0 h-fit w-full overflow-y-hidden grid snap-x snap-mandatory items-stretch gap-[var(--gap)] p-[var(--padding)] list-none;
}

// Paged variant: a horizontal flex track of full-width pages. Each page is its
// own grid filled in reading order, and snaps one page at a time.
.carousel__scrollbox--paged {
  @apply flex flex-nowrap;
}

.carousel__page {
  @apply grid gap-[var(--gap)] snap-start;

  flex: 0 0 100%;
  width: 100%;
}

li {
  @apply m-0 p-0 box-border snap-center;
}
</style>
